import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import { listProducts, type ProductCategory, type ProductSummary } from '../lib/api-client';

const PAGE_SIZE = 10;

const CATEGORY_LABEL: Record<ProductCategory, string> = {
  trip: '行程（trip）',
  experience: '體驗（experience）',
  package: '套票（package）',
  charter: '包船（charter）',
};

function resolveDisplayName(p: ProductSummary): string {
  return p.name_i18n['zh-TW'] ?? p.name_i18n.en ?? p.sku;
}

function formatPrice(amount: string, currency: string): string {
  const num = Number(amount);
  if (!Number.isFinite(num)) return `${currency} ${amount}`;
  return `${currency} ${num.toLocaleString('zh-TW', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function ProductsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const offset = page * PAGE_SIZE;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['products.list', PAGE_SIZE, offset],
    queryFn: () => listProducts({ limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  const totalPages = useMemo(() => {
    if (!data || data.total <= 0) return 1;
    return Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  }, [data]);

  const canPrev = page > 0;
  const canNext = data ? offset + data.items.length < data.total : false;

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">商品管理（A-1）</h1>
        <div className="page-head__actions">
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? '更新中…' : '重新整理'}
          </Button>
          <Button variant="primary" onClick={() => navigate('/products/new')}>
            + 新增商品
          </Button>
        </div>
      </div>

      <p className="page-intro">
        商品 CRUD（AC-A1-1 ~ AC-A1-5）；軟刪除 / 上下架走獨立 endpoint，名稱以 zh-TW 為主顯示。
      </p>

      {isLoading && <div>載入中…</div>}
      {error && (
        <div className="error-banner">
          {(error as Error).message}
          <div style={{ marginTop: 4, fontSize: 12 }}>
            提醒：此頁需 admin token 含 products 權限；若 403 / 404 請確認後端權限設定與 endpoint
            路徑（/products）。
          </div>
        </div>
      )}

      {data && (
        <>
          <div className="table-card">
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>名稱（zh-TW）</th>
                  <th>類別</th>
                  <th>售價</th>
                  <th>時長</th>
                  <th>上架狀態</th>
                  <th style={{ width: 80 }}>動作</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      尚無商品資料
                    </td>
                  </tr>
                )}
                {data.items.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.sku}</td>
                    <td>{resolveDisplayName(p)}</td>
                    <td>{CATEGORY_LABEL[p.category] ?? p.category}</td>
                    <td>{formatPrice(p.base_price, p.currency)}</td>
                    <td>{p.duration_minutes != null ? `${p.duration_minutes} 分` : '—'}</td>
                    <td>
                      {p.deleted_at ? (
                        <Pill tone="danger">已刪除</Pill>
                      ) : p.is_published ? (
                        <Pill tone="success">已上架</Pill>
                      ) : (
                        <Pill tone="warning">草稿</Pill>
                      )}
                    </td>
                    <td>
                      <Link to={`/products/${p.id}`}>編輯</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="list-foot">
            <div>
              共 {data.total} 筆；第 {page + 1} / {totalPages} 頁（每頁 {PAGE_SIZE} 筆）
            </div>
            <div className="page-head__actions">
              <Button
                variant="secondary"
                onClick={() => setPage((n) => Math.max(0, n - 1))}
                disabled={!canPrev}
              >
                上一頁
              </Button>
              <Button variant="secondary" onClick={() => setPage((n) => n + 1)} disabled={!canNext}>
                下一頁
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
